import { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import "./ObjectsPanel.css";
import { CatalogObject, PlacedObject, InventoryItem } from "../../types";
import { clearMessages } from "../../../utils/errorHandling";
import { apiClient } from "@/utils/apiClient/apiClient";
export interface ObjectsPanelProps {
  onCatalogObjectSelect: (object: CatalogObject | null) => void;
  onPlacedObjectSelect: (object: PlacedObject) => void;
  selectedObjectId: string | null | undefined;
  placedObjects: PlacedObject[];
  saveProject: () => void;
  hasUnsavedChanges: () => boolean;
  isSaving: boolean;
  companyId: string;
  // New props for inventory tracking
  onQuantityUsed?: (inventoryId: string, quantityUsed: number) => void;
  quantityUsed?: Map<string, number>; // Map of inventory_id to quantity used
  // New props for placement mode
  objectToPlace: CatalogObject | null;
  // Updated inventory items from deduction
  updatedInventoryItems?: any[] | null;
  onInventoryUpdated?: () => void; // Callback to clear updated inventory items
}

export const ObjectsPanel: FC<ObjectsPanelProps> = ({
  onCatalogObjectSelect,
  onPlacedObjectSelect,
  selectedObjectId,
  placedObjects,
  saveProject,
  hasUnsavedChanges,
  isSaving,
  companyId,
  onQuantityUsed,
  quantityUsed = new Map(),
  objectToPlace,
  updatedInventoryItems,
  onInventoryUpdated,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"catalog" | "placed">("catalog");
  const [catalogObjects, setCatalogObjects] = useState<CatalogObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [error, setError] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  // Retry loading catalog
  const retryLoadCatalog = () => {
    clearMessages(setError, setIsError);
    setLoading(true);
    loadCatalogObjects();
  };

  // Load inventory items for the company
  const loadInventoryItems = async () => {
    try {
      if (!companyId) {
        console.warn("No company ID provided, skipping inventory fetch");
        return [];
      }

      const inventory = await apiClient.listInventory(companyId);
      if (Array.isArray(inventory)) {
        return inventory;
      } else {
        console.error("Invalid inventory response format:", inventory);
        return [];
      }
    } catch (error) {
      console.error("Error loading inventory:", error);
      // Don't fail the entire catalog loading if inventory fails
      return [];
    }
  };

  // Load catalog objects
  const loadCatalogObjects = async () => {
    setLoading(true);
    clearMessages(setError, setIsError);

    try {
      // Use updated inventory items if available, otherwise fetch from API
      let inventoryItems: InventoryItem[] = [];

      if (updatedInventoryItems && updatedInventoryItems.length > 0) {
        // Use the updated inventory items from the deduction
        inventoryItems = updatedInventoryItems;
        console.log("Using updated inventory items:", inventoryItems);
        // Notify parent that we've used the updated inventory items
        if (onInventoryUpdated) {
          onInventoryUpdated();
        }
      } else {
        // Fetch inventory data (which now includes enriched plant/object data)
        const inventoryData = await loadInventoryItems();
        inventoryItems = inventoryData || [];
      }

      // Extract plant and object data from enriched inventory
      const plantItems: any[] = [];
      const objectItems: any[] = [];

      inventoryItems.forEach((item: InventoryItem) => {
        if (item.type === "plant" && item.plant_data) {
          plantItems.push(item.plant_data);
        } else if (item.type === "object" && item.object_data) {
          objectItems.push(item.object_data);
        }
      });

      // If we don't have enough enriched data, fall back to API calls
      if (plantItems.length === 0 || objectItems.length === 0) {
        const [objectsData, plantsData] = await Promise.all([
          (async () => {
            const objects = await apiClient.getObjects(null);
            return Array.isArray(objects) ? objects : [];
          })(),
          (async () => {
            const plants = await apiClient.getPlants(null);
            return Array.isArray(plants) ? plants : [];
          })(),
        ]);
        objectItems.push(...objectsData);
        plantItems.push(...plantsData);
      }

      // Create a map of item_id to inventory status for quick lookup
      const inventoryMap = new Map<string, InventoryItem>();
      inventoryItems.forEach((item: InventoryItem) => {
        const itemId = item.item_id || item.inventory_id;
        if (
          itemId &&
          (item.status === "in_stock" || item.status === "low_stock")
        ) {
          inventoryMap.set(itemId, item);
        }
      });

      // Ensure we have arrays before mapping
      const catalogItems: CatalogObject[] = [
        ...(Array.isArray(objectItems)
          ? objectItems.map((obj: any) => {
              const inventoryItem = inventoryMap.get(obj.object_id);
              return {
                ...obj,
                id: obj.object_id,
                type: "object",
                description: obj.description || "",
                model_3d: obj.model_3d || "",
                category: obj.category || "Uncategorized",
                // Add inventory information
                inventory_id: inventoryItem?.inventory_id,
                quantity: inventoryItem?.quantity || 0,
                status: inventoryItem?.status || "out_of_stock",
                unit_cost: inventoryItem?.unit_cost || 0,
                customer_price: inventoryItem?.selling_price || 0,
                supplier: inventoryItem?.supplier || "",
                location_in_warehouse:
                  inventoryItem?.location_in_warehouse || "",
              };
            })
          : []),
        ...(Array.isArray(plantItems)
          ? plantItems.map((plant: any) => {
              const inventoryItem = inventoryMap.get(plant.plant_id);
              return {
                id: plant.plant_id,
                name: plant.common_name,
                type: "plant",
                description: `A plant from the ${plant.family} family.`,
                model_3d: plant.image_url || "", // Assuming image can be used as a placeholder
                category: plant.family || "Uncategorized",
                // Add inventory information
                inventory_id: inventoryItem?.inventory_id,
                quantity: inventoryItem?.quantity || 0,
                status: inventoryItem?.status || "out_of_stock",
                unit_cost: inventoryItem?.unit_cost || 0,
                customer_price: inventoryItem?.selling_price || 0,
                supplier: inventoryItem?.supplier || "",
                location_in_warehouse:
                  inventoryItem?.location_in_warehouse || "",
              };
            })
          : []),
      ];

      // Filter catalog items to only show those that are in stock
      const inStockCatalogItems = catalogItems.filter((item: CatalogObject) => {
        // If no company ID or no inventory items, show all items (fallback behavior)
        if (!companyId || inventoryItems.length === 0) {
          return true;
        }

        // Check if this item exists in inventory and is in stock
        const inventoryItem = inventoryMap.get(item.id);
        return inventoryItem !== undefined;
      });

      setCatalogObjects(inStockCatalogItems);
    } catch (error) {
      console.error("Error loading catalog:", error);
      // Notify the user of the error
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("admin.errorMessages.failedToLoadCatalogSettings");
      setError(errorMessage);
      setIsError(true);
      setCatalogObjects([]); // Default to empty array on any failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalogObjects();
  }, [companyId, updatedInventoryItems]);

  // Filter catalog objects based on search and category
  const filteredCatalogObjects = catalogObjects.filter((obj) => {
    const matchesSearch = obj.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || obj.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from catalog objects
  const categories = [
    "all",
    ...new Set(catalogObjects.map((obj) => obj.category)),
  ];

  return (
    <div className="objects-panel">
      <div className="panel-header">
        <div>
          <h2 style={{ display: "inline" }}>{t("admin.objects")}</h2>
        </div>
        <div className="panel-header-actions">
          <div className="tab-buttons">
            <button
              className={`tab-button ${
                activeTab === "catalog" ? "active" : ""
              }`}
              onClick={() => setActiveTab("catalog")}
            >
              {t("admin.catalog")}
            </button>
            <button
              className={`tab-button ${activeTab === "placed" ? "active" : ""}`}
              onClick={() => setActiveTab("placed")}
            >
              {t("admin.placed")} ({placedObjects.length})
            </button>
          </div>
        </div>
      </div>

      {activeTab === "catalog" ? (
        <div className="catalog-view">
          <div className="search-filters">
            <input
              type="text"
              placeholder={t("admin.searchObjectsPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              {categories.map((category, i) => (
                <option key={i} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="objects-list">
            {loading ? (
              <div className="loading-message">{t("admin.loading")}</div>
            ) : isError ? (
              <div className="error-message-container">
                <div className="error-icon">⚠️</div>
                <p className="error-message-obj-panel">{error}</p>
                <button
                  className="retry-button"
                  onClick={retryLoadCatalog}
                  disabled={loading}
                >
                  {loading ? t("dashboard.retry") : t("dashboard.tryAgain")}
                </button>
              </div>
            ) : filteredCatalogObjects.length === 0 ? (
              <div className="no-results">{t("admin.noObjects")}</div>
            ) : (
              filteredCatalogObjects.map((obj, i) => {
                const usedQuantity = obj.inventory_id
                  ? quantityUsed.get(obj.inventory_id) || 0
                  : 0;
                const remainingQuantity = (obj.quantity || 0) - usedQuantity;
                const isOutOfStock = remainingQuantity <= 0;

                const isSelected = objectToPlace?.id === obj.id;
                // console.log("OBJ: ", selectedObjectId, " : me: ", obj.id);
                return (
                  <div
                    key={i}
                    className={`catalog-item ${
                      isOutOfStock ? "out-of-stock" : ""
                    } ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      if (isOutOfStock) return;

                      if (isSelected) {
                        // Deselect the item
                        onCatalogObjectSelect(null);
                      } else {
                        // Select the item for placement
                        onCatalogObjectSelect(obj); // sets the object to place
                      }
                    }}
                    style={{
                      cursor: isOutOfStock ? "not-allowed" : "pointer",
                      opacity: isOutOfStock ? 0.6 : 1,
                    }}
                  >
                    <div className="item-icon">
                      {obj.type === "plant" ? "🌿" : "🎯"}
                    </div>
                    <div className="item-details">
                      <h4>{obj.name}</h4>
                      <p>{obj.category}</p>
                      <div className="inventory-info">
                        <div className="inventory-status">
                          <span
                            className={`status-badge ${
                              isOutOfStock ? "out_of_stock" : obj.status
                            }`}
                          >
                            {isOutOfStock
                              ? "OUT OF STOCK"
                              : obj.status?.replace(/_/g, " ").toUpperCase()}
                          </span>
                          <span className="quantity">
                            Qty: {remainingQuantity}
                            {usedQuantity > 0 && (
                              <span className="used-quantity">
                                (Used: {usedQuantity})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="inventory-cost">
                          {obj.customer_price && obj.customer_price > 0 && (
                            <span className="price">
                              ${Number(obj.customer_price).toFixed(2)}
                            </span>
                          )}
                          {obj.unit_cost && obj.unit_cost > 0 && (
                            <span className="cost">
                              Cost: ${Number(obj.unit_cost).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        <div className="placed-objects-view">
          {placedObjects.length === 0 ? (
            <div className="no-results">{t("admin.noObjectsPlaced")}</div>
          ) : (
            <div className="objects-list">
              {placedObjects.map((obj) => (
                <div
                  key={obj.id}
                  className={`placed-item ${
                    selectedObjectId === obj.id ? "selected" : ""
                  }`}
                  onClick={() => onPlacedObjectSelect(obj)}
                >
                  <div className="item-icon">
                    {obj.type === "plant" ? "🌿" : "🎯"}
                  </div>
                  <div className="item-details">
                    <h4>{obj.name}</h4>
                    <p>{obj.type}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      <div className="save-button-container">
        <button
          className="save-button"
          onClick={saveProject}
          disabled={isSaving || !hasUnsavedChanges()}
        >
          {isSaving ? (
            <>
              <span className="spinner"></span>
              {t("admin.formLabels.saving")}
            </>
          ) : (
            t("common.save")
          )}
        </button>
      </div>
    </div>
  );
};
